import { format } from "date-fns";
import { ExternalLink } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { OnboardingTask } from "@forgepoint/shared";

interface TaskChecklistRowProps {
  task: OnboardingTask;
  canInteract: boolean;
  isToggling: boolean;
  userNames: Map<string, string>;
  onToggle: (taskId: string, completed: boolean) => void;
  onOpen?: () => void;
}

/**
 * A single onboarding task row: checkbox, name, assignee, completion
 * attribution, and an optional evidence link. When `onOpen` is set, clicking
 * the row (outside the checkbox and link) opens the task detail sheet.
 */
export function TaskChecklistRow({
  task,
  canInteract,
  isToggling,
  userNames,
  onToggle,
  onOpen,
}: TaskChecklistRowProps) {
  const assigneeName = task.assigneeUserId
    ? userNames.get(task.assigneeUserId)
    : undefined;
  const completedByName = task.completedByUserId
    ? userNames.get(task.completedByUserId)
    : undefined;

  return (
    <div
      className={`flex items-start gap-3 rounded-md px-2 py-2 hover:bg-muted/50 ${onOpen ? "cursor-pointer" : ""}`}
      onClick={onOpen}
    >
      <span onClick={(event) => event.stopPropagation()}>
        <Checkbox
          checked={task.completed}
          disabled={!canInteract || isToggling}
          onCheckedChange={(checked) => onToggle(task.id, checked === true)}
          className="mt-0.5"
        />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className={
              task.completed
                ? "text-sm text-muted-foreground line-through"
                : "text-sm"
            }
          >
            {task.name}
          </span>
          {task.linkUrl && (
            <a
              href={task.linkUrl}
              target="_blank"
              rel="noreferrer"
              className="text-muted-foreground hover:text-foreground"
              onClick={(event) => event.stopPropagation()}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          {task.completed && task.completedAt
            ? `✓ ${completedByName ?? "Someone"} · ${format(task.completedAt, "MMM d")}`
            : assigneeName
              ? `Owner: ${assigneeName}`
              : null}
        </div>
      </div>
    </div>
  );
}
